/**
 * Permissions Redux Slice - B2C Merchant Portal
 *
 * Manages the lifecycle of the user's permission snapshot:
 *   idle → loading → resolved / error
 *
 * Production hardening: TTL-based cache, retry with exponential backoff,
 * stale-while-revalidate on transient failures.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPermissionSnapshot } from '../../services/permissionsService';

/* ================================================================
 * Constants
 * ================================================================ */

/** Permission cache TTL - 15 minutes. */
export const CACHE_TTL_MS = 15 * 60 * 1000;

/** Max retry attempts on transient failures. */
const MAX_RETRIES = 3;

/** Base delay for exponential backoff (ms). */
const RETRY_BASE_MS = 1000;

/** Minimum interval between refreshes to prevent rapid re-fetches (ms). */
export const REFRESH_DEBOUNCE_MS = 5000;

/* ================================================================
 * Helpers
 * ================================================================ */

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const backoff = (attempt) => RETRY_BASE_MS * 2 ** (attempt - 1);

/* ================================================================
 * Async Thunk
 * ================================================================ */

export const loadPermissions = createAsyncThunk(
  'permissions/load',
  async (_, { rejectWithValue }) => {
    let lastError = 'Failed to load permissions';

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        return await fetchPermissionSnapshot();
      } catch (err) {
        lastError =
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load permissions';

        // Don't retry on 401/403 - auth failures, not transient
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          return rejectWithValue(lastError);
        }

        if (attempt < MAX_RETRIES) {
          await sleep(backoff(attempt));
        }
      }
    }

    return rejectWithValue(lastError);
  }
);

/* ================================================================
 * Slice
 * ================================================================ */

const initialState = {
  /** The resolved permission snapshot from the IAM backend. */
  snapshot: null,
  /** Lifecycle status: 'idle' | 'loading' | 'resolved' | 'error'. */
  status: 'idle',
  /** Error message if the fetch failed. */
  error: null,
  /** Timestamp (ms) of the last successful fetch. */
  fetchedAt: null,
};

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearPermissions(state) {
      state.snapshot = null;
      state.status = 'idle';
      state.error = null;
      state.fetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPermissions.pending, (state) => {
        // Stale-while-revalidate: keep old snapshot visible during refresh
        if (!state.snapshot) {
          state.status = 'loading';
        }
        state.error = null;
      })
      .addCase(loadPermissions.fulfilled, (state, action) => {
        state.snapshot = action.payload;
        state.status = 'resolved';
        state.error = null;
        state.fetchedAt = Date.now();
      })
      .addCase(loadPermissions.rejected, (state, action) => {
        state.error = action.payload || 'Failed to load permissions';
        // Keep existing snapshot on transient failures (stale-while-revalidate)
        if (state.snapshot) {
          state.status = 'resolved';
        } else {
          state.status = 'error';
        }
      })
      // Wipe the snapshot on logout so a subsequent login never sees stale
      // permissions. Matched by action type string to avoid an import cycle
      // with authSlice (which imports this slice's thunks).
      .addCase('auth/logout', (state) => {
        state.snapshot = null;
        state.status = 'idle';
        state.error = null;
        state.fetchedAt = null;
      });
  },
});

/** Returns true if the permission cache has expired past the TTL. */
export const selectIsPermissionStale = (state) => {
  const { fetchedAt } = state.permissions;
  if (!fetchedAt) return true;
  return Date.now() - fetchedAt > CACHE_TTL_MS;
};

export const { clearPermissions } = permissionsSlice.actions;
export default permissionsSlice.reducer;
